"use client";

import {
  AlertTriangle,
  ArrowRight,
  Bot,
  CheckCircle2,
  Clock3,
  Database,
  FileCheck2,
  ShieldCheck,
  Table2,
} from "lucide-react";

import type { IndustryData } from "./data";
import {
  AskWayak,
  KpiCard,
  PageHeader,
  ProductFrame,
  StatusPill,
} from "./ProductPreview";

type Tone = "green" | "sky" | "amber" | "rose" | "slate";

type DemoRow = {
  id: string;
  title: string;
  detail: string;
  value: string;
  tone: Tone;
};

type Scenario = {
  metrics: [[string, string], [string, string], [string, string]];
  rows: [DemoRow, DemoRow];
  finding: string;
  action: string;
  owner: string;
};

type IndustryDemo = {
  workspace: string;
  scenarios: [Scenario, Scenario, Scenario];
};

const DEMOS: Record<string, IndustryDemo> = {
  manufacturing: {
    workspace: "North Plant operations",
    scenarios: [
      {
        metrics: [
          ["184", "Work orders watched"],
          ["$42.8k", "Margin at risk"],
          ["7", "Exceptions today"],
        ],
        rows: [
          {
            id: "WO-10482",
            title: "Axle housing · Line 3",
            detail: "Actual cost 11.8% above standard",
            value: "+$1,950",
            tone: "rose",
          },
          {
            id: "WO-10471",
            title: "Rotor assembly · Line 1",
            detail: "Scrap drift isolated to shift B",
            value: "+4.2%",
            tone: "amber",
          },
        ],
        finding:
          "Material usage explains 6.2 points of the variance; scrap and labor account for the remainder.",
        action:
          "Hold closeout and route the evidence pack to plant finance before shift end.",
        owner: "Plant finance review",
      },
      {
        metrics: [
          ["438", "Assets watched"],
          ["78%", "Highest failure risk"],
          ["12h", "Action window"],
        ],
        rows: [
          {
            id: "CNC-14",
            title: "Cell 4 spindle",
            detail: "Vibration 22% above rolling baseline",
            value: "78% risk",
            tone: "rose",
          },
          {
            id: "PRESS-07",
            title: "Stamping press",
            detail: "Oil temperature trending upward",
            value: "Inspect",
            tone: "amber",
          },
        ],
        finding:
          "The CNC-14 pattern matches two prior bearing events and is accelerating under peak load.",
        action:
          "Recommend an inspection inside 12 hours; maintenance planner approves the window.",
        owner: "Maintenance planner",
      },
      {
        metrics: [
          ["76", "BOMs reconciled"],
          ["$31.6k", "Projected impact"],
          ["8.4%", "Usage variance"],
        ],
        rows: [
          {
            id: "BOM-778",
            title: "Aluminum housing",
            detail: "Supplier lot AL-4482 above expected use",
            value: "+8.4%",
            tone: "rose",
          },
          {
            id: "BOM-624",
            title: "Seal kit",
            detail: "Price variance crossed review threshold",
            value: "+$4.1k",
            tone: "amber",
          },
        ],
        finding:
          "The variance began with the latest supplier lot and is concentrated on two production cells.",
        action:
          "Create a procurement review with the affected work orders and lot evidence attached.",
        owner: "Procurement review",
      },
    ],
  },
  "customer-service": {
    workspace: "Customer operations",
    scenarios: [
      {
        metrics: [
          ["428", "Open conversations"],
          ["96%", "Auto-triaged"],
          ["14", "SLA at risk"],
        ],
        rows: [
          {
            id: "ZD-18492",
            title: "Northstar Logistics",
            detail: "24m to breach · negative sentiment",
            value: "Enterprise",
            tone: "rose",
          },
          {
            id: "ZD-18488",
            title: "Atlas Retail",
            detail: "Billing issue · account context linked",
            value: "Priority",
            tone: "amber",
          },
        ],
        finding:
          "Northstar has an active incident, usage is down 38%, and the account has two prior escalations.",
        action:
          "Route to Enterprise Support, notify the account owner, and start the SLA clock playbook.",
        owner: "Support lead",
      },
      {
        metrics: [
          ["63", "Drafts ready"],
          ["2", "Sources cited"],
          ["91%", "First-pass acceptance"],
        ],
        rows: [
          {
            id: "ZD-18506",
            title: "Webhook delivery delay",
            detail: "Incident status and account history cited",
            value: "Draft ready",
            tone: "green",
          },
          {
            id: "ZD-18502",
            title: "Role permission question",
            detail: "Admin guide and workspace policy cited",
            value: "Review",
            tone: "sky",
          },
        ],
        finding:
          "The response is grounded in the current incident status, the customer tier, and two approved articles.",
        action:
          "Present the draft with citations and tone check for a support agent to approve.",
        owner: "Agent approval",
      },
      {
        metrics: [
          ["8", "Accounts at risk"],
          ["14", "Escalation signals"],
          ["3", "Owners notified"],
        ],
        rows: [
          {
            id: "ACCT-228",
            title: "Northstar Logistics",
            detail: "Usage -38% · 3 unresolved tickets",
            value: "High risk",
            tone: "rose",
          },
          {
            id: "ACCT-194",
            title: "Meridian Foods",
            detail: "Renewal in 34 days · CSAT falling",
            value: "Watch",
            tone: "amber",
          },
        ],
        finding:
          "The risk pattern combines ticket volume, sentiment, product events, and renewal timing.",
        action:
          "Generate an account brief and assign a coordinated support and success follow-up.",
        owner: "Customer success",
      },
    ],
  },
  insurance: {
    workspace: "Claims operations",
    scenarios: [
      {
        metrics: [
          ["126", "Claims received"],
          ["$87.4k", "Payout held"],
          ["11", "Investigator review"],
        ],
        rows: [
          {
            id: "CLM-28417",
            title: "Auto collision",
            detail: "Duplicate invoice · estimate mismatch 24%",
            value: "86 / 100",
            tone: "rose",
          },
          {
            id: "CLM-28409",
            title: "Property water loss",
            detail: "Vendor and address linkage detected",
            value: "72 / 100",
            tone: "amber",
          },
        ],
        finding:
          "The top claim combines a duplicate invoice, repair-estimate mismatch, and a prior vendor linkage.",
        action:
          "Hold payment and route the complete evidence set to an investigator for decision.",
        owner: "Investigator decision",
      },
      {
        metrics: [
          ["36", "Documents extracted"],
          ["2", "Missing items"],
          ["82", "Straight-through"],
        ],
        rows: [
          {
            id: "FNOL-8812",
            title: "Auto glass loss",
            detail: "Policy and incident fields complete",
            value: "Ready",
            tone: "green",
          },
          {
            id: "FNOL-8808",
            title: "Commercial property",
            detail: "Loss date confirmation missing",
            value: "Needs info",
            tone: "amber",
          },
        ],
        finding:
          "Wayak structured the intake across email and attachments and found one required field missing.",
        action:
          "Request the missing detail, then route the complete record to the correct adjuster queue.",
        owner: "Adjuster routing",
      },
      {
        metrics: [
          ["94", "Policies checked"],
          ["7", "Terms flagged"],
          ["100%", "Evidence linked"],
        ],
        rows: [
          {
            id: "POL-7739",
            title: "Water loss coverage",
            detail: "$2,500 deductible · endorsement matched",
            value: "Covered",
            tone: "green",
          },
          {
            id: "POL-7698",
            title: "Vehicle rental extension",
            detail: "Limit requires adjuster interpretation",
            value: "Review",
            tone: "amber",
          },
        ],
        finding:
          "The recommendation is tied to the policy term, active endorsement, loss date, and deductible.",
        action:
          "Send the evidence-linked recommendation to the adjuster before payout continues.",
        owner: "Adjuster approval",
      },
    ],
  },
  hr: {
    workspace: "People operations",
    scenarios: [
      {
        metrics: [
          ["214", "Applicants organized"],
          ["38", "Skills matched"],
          ["12", "Recruiter review"],
        ],
        rows: [
          {
            id: "APP-1824",
            title: "Operations analyst",
            detail: "Required experience mapped to role rubric",
            value: "Review",
            tone: "sky",
          },
          {
            id: "APP-1818",
            title: "Data engineer",
            detail: "Portfolio evidence linked · gap noted",
            value: "Review",
            tone: "amber",
          },
        ],
        finding:
          "Wayak organizes job evidence against the recruiter-authored rubric without making the hiring decision.",
        action:
          "Prepare a consistent evidence packet for recruiter review; no candidate is auto-rejected.",
        owner: "Recruiter decision",
      },
      {
        metrics: [
          ["32", "New hires"],
          ["94%", "Tasks complete"],
          ["5", "Blocked tasks"],
        ],
        rows: [
          {
            id: "COHORT-0812",
            title: "August 12 cohort",
            detail: "Laptop approval pending for 3 hires",
            value: "Blocked",
            tone: "amber",
          },
          {
            id: "PAYROLL-32",
            title: "Payroll enrollment",
            detail: "32 of 32 records validated",
            value: "Complete",
            tone: "green",
          },
        ],
        finding:
          "Most onboarding is complete; equipment approvals are the only shared blocker for the cohort.",
        action:
          "Notify the equipment owner and keep each hire’s checklist and evidence in one run.",
        owner: "People ops owner",
      },
      {
        metrics: [
          ["12", "Team signals"],
          ["3", "Cohorts to review"],
          ["0", "Individual decisions"],
        ],
        rows: [
          {
            id: "TEAM-OPS",
            title: "Operations cohort",
            detail: "Tenure and workload trend changed",
            value: "Review",
            tone: "amber",
          },
          {
            id: "TEAM-CS",
            title: "Customer success cohort",
            detail: "Engagement stable across the quarter",
            value: "Stable",
            tone: "green",
          },
        ],
        finding:
          "The view uses aggregate team trends and never produces an automated employment decision.",
        action:
          "Give People leadership the trend evidence and suggested questions for a human-led review.",
        owner: "People leadership",
      },
    ],
  },
  retail: {
    workspace: "Merchandising operations",
    scenarios: [
      {
        metrics: [
          ["1,284", "SKUs watched"],
          ["12", "Stockout risks"],
          ["43", "Reorders proposed"],
        ],
        rows: [
          {
            id: "SKU-04182",
            title: "Oat milk · Store 014",
            detail: "1.3 days cover · 4-day lead time",
            value: "24 cases",
            tone: "rose",
          },
          {
            id: "SKU-08211",
            title: "Sparkling water · Store 022",
            detail: "Demand 18% above plan",
            value: "12 cases",
            tone: "amber",
          },
        ],
        finding:
          "The recommended quantity balances expected demand, supplier lead time, and store-level safety stock.",
        action:
          "Place proposed orders in the buyer queue; the merchandising owner approves release.",
        owner: "Buyer approval",
      },
      {
        metrics: [
          ["18", "Promotions measured"],
          ["6.4%", "Best margin lift"],
          ["3", "Underperforming"],
        ],
        rows: [
          {
            id: "PROMO-118",
            title: "Weekend beverage bundle",
            detail: "Volume +14% · margin +6.4%",
            value: "Scale",
            tone: "green",
          },
          {
            id: "PROMO-109",
            title: "Two-for-one snacks",
            detail: "Cannibalization offsets unit growth",
            value: "Stop",
            tone: "rose",
          },
        ],
        finding:
          "Wayak separates incremental demand from shifted sales and reconciles the result to margin.",
        action:
          "Draft the next promotion plan with store segments and evidence for merchant review.",
        owner: "Merchant review",
      },
      {
        metrics: [
          ["426", "Prices modeled"],
          ["$18.6k", "Weekly opportunity"],
          ["27", "Recommendations"],
        ],
        rows: [
          {
            id: "SKU-1914",
            title: "Premium coffee 12oz",
            detail: "Low sensitivity in urban stores",
            value: "+2.0%",
            tone: "green",
          },
          {
            id: "SKU-3380",
            title: "Family cereal",
            detail: "High switching risk above current price",
            value: "Hold",
            tone: "slate",
          },
        ],
        finding:
          "Recommendations combine demand response, competitive position, and margin guardrails.",
        action:
          "Send bounded price changes to merchandising; no price is published without approval.",
        owner: "Pricing approval",
      },
    ],
  },
  logistics: {
    workspace: "Network control tower",
    scenarios: [
      {
        metrics: [
          ["612", "Live loads"],
          ["18", "At risk"],
          ["4", "Critical"],
        ],
        rows: [
          {
            id: "LD-5821",
            title: "Laredo → Dallas",
            detail: "Dwell 52m · ETA +97m",
            value: "Critical",
            tone: "rose",
          },
          {
            id: "LD-5816",
            title: "Memphis → Atlanta",
            detail: "Weather corridor risk increasing",
            value: "+41m",
            tone: "amber",
          },
        ],
        finding:
          "The top delay combines terminal dwell, current traffic, carrier history, and the delivery window.",
        action:
          "Assign a dispatcher, notify the receiving team, and propose the best recovery route.",
        owner: "Dispatcher control",
      },
      {
        metrics: [
          ["24", "Carriers scored"],
          ["96.2%", "Network on-time"],
          ["3", "Lanes to reassign"],
        ],
        rows: [
          {
            id: "LANE-DFW-HOU",
            title: "Dallas → Houston",
            detail: "Carrier A leads cost and reliability",
            value: "Preferred",
            tone: "green",
          },
          {
            id: "LANE-LRD-DAL",
            title: "Laredo → Dallas",
            detail: "Tender rejection and dwell rising",
            value: "Review",
            tone: "amber",
          },
        ],
        finding:
          "The scorecard normalizes on-time, rejection, claims, and cost by lane rather than network average.",
        action:
          "Prepare lane-level allocation changes for the transportation manager to approve.",
        owner: "Transport manager",
      },
      {
        metrics: [
          ["4 weeks", "Rolling forecast"],
          ["18 loads", "Peak gap"],
          ["3", "Capacity options"],
        ],
        rows: [
          {
            id: "WK-42-SOUTH",
            title: "South region peak",
            detail: "Demand above committed capacity",
            value: "+18 loads",
            tone: "rose",
          },
          {
            id: "WK-43-WEST",
            title: "West region",
            detail: "Capacity aligned to forecast",
            value: "Covered",
            tone: "green",
          },
        ],
        finding:
          "The gap is concentrated on two lanes after promotions and inbound appointments are combined.",
        action:
          "Open the approved backup-carrier playbook and present costed options to planning.",
        owner: "Network planning",
      },
    ],
  },
  finance: {
    workspace: "Risk and reporting",
    scenarios: [
      {
        metrics: [
          ["$1.28B", "Exposure watched"],
          ["84%", "Limit utilization"],
          ["2", "Breaches"],
        ],
        rows: [
          {
            id: "LIMIT-IND",
            title: "Industrials concentration",
            detail: "12.8% exposure vs 12.0% limit",
            value: "+80 bps",
            tone: "rose",
          },
          {
            id: "LIMIT-FX",
            title: "Emerging-market FX",
            detail: "Stress utilization reached review band",
            value: "Watch",
            tone: "amber",
          },
        ],
        finding:
          "The industrials breach reconciles positions, market data, hierarchy, and the approved policy limit.",
        action:
          "Create a source-traced breach packet and pause downstream reporting for risk sign-off.",
        owner: "Risk sign-off",
      },
      {
        metrics: [
          ["1", "Report due"],
          ["100%", "Numbers traced"],
          ["4", "Reconciliations open"],
        ],
        rows: [
          {
            id: "REP-LCR-07",
            title: "Liquidity coverage report",
            detail: "Two source reconciliations need review",
            value: "Draft",
            tone: "amber",
          },
          {
            id: "REP-CAP-06",
            title: "Capital adequacy",
            detail: "All controls passed and evidence linked",
            value: "Ready",
            tone: "green",
          },
        ],
        finding:
          "Every reported value is linked to the source view, transformation, control, and prior-period movement.",
        action:
          "Route only the unresolved reconciliations to Finance before final sign-off.",
        owner: "Controller approval",
      },
      {
        metrics: [
          ["6.2M", "Entries scanned"],
          ["17", "Anomalies"],
          ["3.6σ", "Largest deviation"],
        ],
        rows: [
          {
            id: "JE-881204",
            title: "Manual close entry",
            detail: "Unusual approver and posting time",
            value: "3.6σ",
            tone: "rose",
          },
          {
            id: "JE-881117",
            title: "Intercompany allocation",
            detail: "Amount differs from recurring pattern",
            value: "Review",
            tone: "amber",
          },
        ],
        finding:
          "The anomalies are ranked using amount, timing, approver path, account history, and close context.",
        action:
          "Attach the evidence and route the entries to the controller; Wayak does not post reversals.",
        owner: "Controller review",
      },
    ],
  },
  healthcare: {
    workspace: "Care operations",
    scenarios: [
      {
        metrics: [
          ["67", "Encounters"],
          ["61", "Drafts ready"],
          ["4", "Missing signatures"],
        ],
        rows: [
          {
            id: "ENC-8842",
            title: "De-identified encounter",
            detail: "Note draft and evidence ready",
            value: "Review",
            tone: "sky",
          },
          {
            id: "ENC-8836",
            title: "De-identified encounter",
            detail: "Clinician signature still required",
            value: "Blocked",
            tone: "amber",
          },
        ],
        finding:
          "The draft organizes the encounter record and supporting evidence; it does not make a clinical judgment.",
        action:
          "Present the draft and suggested codes to the clinician for review and signature.",
        owner: "Clinician approval",
      },
      {
        metrics: [
          ["42", "Intakes today"],
          ["7", "Incomplete"],
          ["0", "Clinical decisions"],
        ],
        rows: [
          {
            id: "INT-3041",
            title: "New patient intake",
            detail: "Insurance image unreadable",
            value: "Needs info",
            tone: "amber",
          },
          {
            id: "INT-3034",
            title: "Follow-up intake",
            detail: "Demographics and consent validated",
            value: "Ready",
            tone: "green",
          },
        ],
        finding:
          "Wayak checks administrative completeness across forms and attachments before the visit.",
        action:
          "Request only the missing administrative item and update the scheduling queue.",
        owner: "Front desk review",
      },
      {
        metrics: [
          ["218", "Controls checked"],
          ["3", "Access exceptions"],
          ["100%", "Evidence retained"],
        ],
        rows: [
          {
            id: "ACC-772",
            title: "After-hours chart access",
            detail: "Access pattern outside assigned unit",
            value: "Review",
            tone: "rose",
          },
          {
            id: "DOC-190",
            title: "Required attestation",
            detail: "Two records missing acknowledgement",
            value: "Follow up",
            tone: "amber",
          },
        ],
        finding:
          "The exception combines access logs, role scope, schedule, and the organization’s approved policy.",
        action:
          "Route the evidence to compliance; no care or access decision is automated.",
        owner: "Compliance review",
      },
    ],
  },
  energy: {
    workspace: "Grid operations",
    scenarios: [
      {
        metrics: [
          ["6.8 GW", "Peak forecast"],
          ["2.4%", "Forecast error"],
          ["4h", "Peak window"],
        ],
        rows: [
          {
            id: "ZONE-NORTH",
            title: "North load zone",
            detail: "Temperature-adjusted demand above plan",
            value: "+210 MW",
            tone: "amber",
          },
          {
            id: "ZONE-CENTRAL",
            title: "Central load zone",
            detail: "Capacity aligned with reserve target",
            value: "Covered",
            tone: "green",
          },
        ],
        finding:
          "The revised peak combines weather, meter trends, outages, and large-customer schedules.",
        action:
          "Present reserve and demand-response options to the system operator for dispatch decision.",
        owner: "Operator dispatch",
      },
      {
        metrics: [
          ["438", "Assets watched"],
          ["7", "At risk"],
          ["78%", "Highest risk"],
        ],
        rows: [
          {
            id: "TX-204",
            title: "Transmission transformer",
            detail: "Temperature 12°C above baseline · load 91%",
            value: "78% risk",
            tone: "rose",
          },
          {
            id: "SW-118",
            title: "Substation switch",
            detail: "Operation count near service threshold",
            value: "Plan",
            tone: "amber",
          },
        ],
        finding:
          "TX-204 is deviating from its own thermal baseline under a load pattern seen before prior faults.",
        action:
          "Recommend an inspection window and route it to asset management for approval.",
        owner: "Asset manager",
      },
      {
        metrics: [
          ["12,840", "Customers affected"],
          ["6", "Crews active"],
          ["18m", "Next update"],
        ],
        rows: [
          {
            id: "OUT-447",
            title: "North feeder outage",
            detail: "Likely vegetation contact · crew 12 assigned",
            value: "Restoring",
            tone: "sky",
          },
          {
            id: "OUT-442",
            title: "Central switching event",
            detail: "Remote isolation complete",
            value: "Contained",
            tone: "green",
          },
        ],
        finding:
          "Wayak joins SCADA events, GIS, weather, crew location, and customer impact into one incident view.",
        action:
          "Draft the restoration sequence and customer update for the control-room lead to approve.",
        owner: "Control-room lead",
      },
    ],
  },
  "real-estate": {
    workspace: "Portfolio operations",
    scenarios: [
      {
        metrics: [
          ["318", "Leases watched"],
          ["14", "Expiring in 90d"],
          ["$126k", "NOI at risk"],
        ],
        rows: [
          {
            id: "LEASE-410",
            title: "Parkview Tower · Suite 410",
            detail: "Notice deadline in 18 days · 3% CPI step",
            value: "Act now",
            tone: "rose",
          },
          {
            id: "LEASE-218",
            title: "Harbor Center · Suite 218",
            detail: "Renewal terms prepared from lease",
            value: "Review",
            tone: "sky",
          },
        ],
        finding:
          "The Parkview lease contains an upcoming notice deadline and an unindexed rent step affecting NOI.",
        action:
          "Create the renewal brief with exact clause citations and assign the property owner.",
        owner: "Asset manager",
      },
      {
        metrics: [
          ["26", "Files organized"],
          ["4", "Missing items"],
          ["0", "Auto-decisions"],
        ],
        rows: [
          {
            id: "APP-771",
            title: "Tenant application",
            detail: "Income verification missing",
            value: "Incomplete",
            tone: "amber",
          },
          {
            id: "APP-768",
            title: "Tenant application",
            detail: "Required file set complete",
            value: "Human review",
            tone: "sky",
          },
        ],
        finding:
          "Wayak assembles the required evidence consistently but never makes an automated housing decision.",
        action:
          "Request the missing document or send the complete packet to the authorized reviewer.",
        owner: "Authorized reviewer",
      },
      {
        metrics: [
          ["42", "Properties reconciled"],
          ["3", "Exceptions"],
          ["100%", "Source linked"],
        ],
        rows: [
          {
            id: "PROP-014",
            title: "Parkview Tower",
            detail: "CAM recovery differs from ledger",
            value: "+$18.4k",
            tone: "amber",
          },
          {
            id: "PROP-009",
            title: "Harbor Center",
            detail: "Occupancy and revenue reconciled",
            value: "Ready",
            tone: "green",
          },
        ],
        finding:
          "The owner report ties occupancy, rent roll, recoveries, and general ledger back to source.",
        action:
          "Route only the three exceptions to accounting before the report is published.",
        owner: "Accounting approval",
      },
    ],
  },
};

export function IndustryProductPreview({
  data,
  exampleIndex = 0,
  className = "",
}: {
  data: IndustryData;
  exampleIndex?: number;
  className?: string;
}) {
  const demo = DEMOS[data.slug] ?? DEMOS.manufacturing;
  const safeIndex = Math.min(Math.max(exampleIndex, 0), 2) as 0 | 1 | 2;
  const scenario = demo.scenarios[safeIndex];
  const example = data.examples[safeIndex];

  return (
    <ProductFrame
      active="records"
      className={`pv-stage-frame ip-preview ${className}`}
    >
      <div className="pv-page ip-page">
        <PageHeader
          icon={Table2}
          title={example.title}
          description={`${demo.workspace} · illustrative Wayak workflow`}
        />
        <div className="ip-preview-meta">
          <span>Sanitized demo data</span>
          <StatusPill tone="slate">Human-controlled</StatusPill>
        </div>
        <AskWayak
          placeholder={`Ask Wayak what needs attention in ${example.title.toLowerCase()}...`}
        />
        <div className="ip-kpis">
          <KpiCard
            label={scenario.metrics[0][1]}
            value={scenario.metrics[0][0]}
            icon={Database}
          />
          <KpiCard
            label={scenario.metrics[1][1]}
            value={scenario.metrics[1][0]}
            icon={AlertTriangle}
            tone="rose"
          />
          <KpiCard
            label={scenario.metrics[2][1]}
            value={scenario.metrics[2][0]}
            icon={Clock3}
            tone="sky"
          />
        </div>
        <div className="ip-review-grid">
          <section className="ip-records">
            <div className="ip-panel-heading">
              <span>
                <Table2 size={12} /> Records requiring attention
              </span>
              <span>Updated now</span>
            </div>
            {scenario.rows.map((row) => (
              <div className="ip-record" key={row.id}>
                <span className="ip-record-mark">
                  <FileCheck2 size={12} />
                </span>
                <span className="ip-record-copy">
                  <small>{row.id}</small>
                  <strong>{row.title}</strong>
                  <em>{row.detail}</em>
                </span>
                <span className="ip-record-value">
                  <strong>{row.value}</strong>
                  <StatusPill tone={row.tone}>Review</StatusPill>
                </span>
              </div>
            ))}
          </section>
          <aside className="ip-context">
            <div className="ip-panel-heading">
              <span>
                <Bot size={12} /> Wayak context
              </span>
              <StatusPill tone="green">Ready</StatusPill>
            </div>
            <div className="ip-source-chips">
              {example.sources.map((source) => (
                <span key={source}>{source}</span>
              ))}
            </div>
            <div className="ip-finding">
              <small>Agent finding</small>
              <p>{scenario.finding}</p>
            </div>
            <div className="ip-next-action">
              <span>
                <ShieldCheck size={13} />
              </span>
              <div>
                <small>Next controlled action</small>
                <p>{scenario.action}</p>
                <strong>
                  {scenario.owner} <ArrowRight size={10} />
                </strong>
              </div>
            </div>
          </aside>
        </div>
        <div className="ip-runline" aria-hidden="true">
          {["Connect", "Evaluate", "Human gate", "Writeback"].map(
            (step, index) => (
              <span
                key={step}
                className={
                  index < 2 ? "is-done" : index === 2 ? "is-active" : ""
                }
              >
                {index < 2 ? <CheckCircle2 size={11} /> : <i>{index + 1}</i>}
                {step}
              </span>
            ),
          )}
        </div>
      </div>
    </ProductFrame>
  );
}

export function IndustryProductPreviewStyles() {
  return (
    <style>{`
      @keyframes ipRecordEnter { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }
      @keyframes ipContextEnter { from { opacity: 0; transform: translateX(8px); } to { opacity: 1; transform: none; } }
      @keyframes ipGateHold { 0%,50%,100% { background: var(--pv-soft); box-shadow: inset 0 0 0 0 rgba(9,9,11,0); } 62%,78% { background: #ececee; box-shadow: inset 0 -2px 0 #18181b; } }

      .ip-preview { margin-top: 0; min-height: 0; }
      .ip-page { padding-bottom: 18px !important; }
      .ip-preview-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 13px; color: var(--pv-muted); font-size: 7px; letter-spacing: .09em; text-transform: uppercase; }
      .ip-kpis { display: grid; grid-template-columns: repeat(3,1fr); gap: 7px; margin-top: 10px; }
      .ip-review-grid { display: grid; grid-template-columns: 1.25fr .75fr; gap: 8px; margin-top: 9px; }
      .ip-records, .ip-context { min-width: 0; padding: 11px; border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; }
      .ip-panel-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-bottom: 8px; border-bottom: 1px solid var(--pv-border); color: var(--pv-muted); font-size: 7px; }
      .ip-panel-heading > span:first-child { display: flex; align-items: center; gap: 5px; color: var(--pv-ink); font-size: 8px; font-weight: 700; }
      .ip-record { display: grid; grid-template-columns: 29px minmax(0,1fr) auto; align-items: center; gap: 8px; padding: 9px 0; border-bottom: 1px solid #efeff1; animation: ipRecordEnter .42s cubic-bezier(.16,1,.3,1) both; }
      .ip-record:nth-child(3) { animation-delay: .07s; }
      .ip-record:nth-child(4) { animation-delay: .14s; }
      .ip-record:last-child { border-bottom: 0; padding-bottom: 0; }
      .ip-record-mark { width: 29px; height: 29px; display: grid; place-items: center; border-radius: 8px; background: var(--pv-soft); color: #27272a; }
      .ip-record-copy { min-width: 0; }
      .ip-record-copy small, .ip-record-copy strong, .ip-record-copy em { display: block; }
      .ip-record-copy small { color: #a1a1aa; font-size: 6px; letter-spacing: .08em; }
      .ip-record-copy strong { overflow: hidden; margin-top: 2px; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
      .ip-record-copy em { overflow: hidden; margin-top: 2px; color: var(--pv-muted); font-size: 7px; font-style: normal; text-overflow: ellipsis; white-space: nowrap; }
      .ip-record-value { display: grid; justify-items: end; gap: 4px; }
      .ip-record-value > strong { font-size: 8px; }
      .ip-context { animation: ipContextEnter .48s cubic-bezier(.16,1,.3,1) .12s both; }
      .ip-source-chips { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 9px; }
      .ip-source-chips span { padding: 4px 5px; border: 1px solid var(--pv-border); border-radius: 5px; background: var(--pv-soft); color: #52525b; font-size: 6px; }
      .ip-finding { margin-top: 10px; padding: 9px; border-radius: 9px; background: var(--pv-soft); }
      .ip-finding small, .ip-next-action small { color: var(--pv-muted); font-size: 6px; letter-spacing: .08em; text-transform: uppercase; }
      .ip-finding p, .ip-next-action p { margin: 5px 0 0; color: #3f3f46; font-size: 7px; line-height: 1.45; }
      .ip-next-action { display: grid; grid-template-columns: 24px 1fr; gap: 7px; margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--pv-border); }
      .ip-next-action > span { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 7px; background: #18181b; color: #fff; }
      .ip-next-action strong { display: flex; align-items: center; gap: 4px; margin-top: 6px; font-size: 7px; }
      .ip-runline { display: grid; grid-template-columns: repeat(4,1fr); margin-top: 9px; overflow: hidden; border: 1px solid var(--pv-border); border-radius: 10px; }
      .ip-runline > span { position: relative; display: flex; align-items: center; gap: 5px; padding: 7px; border-right: 1px solid var(--pv-border); color: #a1a1aa; font-size: 7px; }
      .ip-runline > span:last-child { border-right: 0; }
      .ip-runline i { width: 12px; height: 12px; display: grid; place-items: center; border: 1px solid #d4d4d8; border-radius: 50%; font-size: 6px; font-style: normal; }
      .ip-runline .is-done { color: #166534; }
      .ip-runline .is-active { background: var(--pv-soft); color: #18181b; font-weight: 700; animation: ipGateHold 4.2s ease-in-out infinite; }

      @media (max-width: 820px) {
        .ip-review-grid { grid-template-columns: 1fr; }
        .ip-context { display: block; }
      }
      @media (max-width: 520px) {
        .ip-kpis { grid-template-columns: 1fr 1fr; }
        .ip-kpis > :last-child { grid-column: 1 / -1; }
        .ip-runline { grid-template-columns: 1fr 1fr; }
        .ip-runline > span:nth-child(2) { border-right: 0; }
        .ip-runline > span:nth-child(n+3) { border-top: 1px solid var(--pv-border); }
      }
      @media (prefers-reduced-motion: reduce) { .ip-record,.ip-context,.ip-runline .is-active { animation: none !important; transform: none !important; } }
    `}</style>
  );
}
